import { useState, useMemo } from 'react';
import { useTypewriter } from '../hooks/useTypewriter';
import { QueryBlock } from './QueryBlock';
import type { ReasoningStep, ToolCallRecord } from '@cade/shared-types';
import type { QueryLanguage } from '@cade/syntax-highlight';

interface ToolCallEntry {
  toolName: string;
  toolUseId: string;
  status: 'running' | 'success' | 'error';
  summary?: string;
  inputPayload?: Record<string, unknown>;
  queryLanguage?: string;
  resultSummary?: string;
  latencyMs?: number;
}

interface TraceEntry {
  type: 'thinking' | 'tool';
  text?: string;
  toolUseId?: string;
}

interface Props {
  // Live streaming mode
  reasoningText?: string;
  toolCalls?: Map<string, ToolCallEntry>;
  traceEntries?: TraceEntry[];
  isStreaming?: boolean;
  // Persisted mode
  persistedTrace?: ReasoningStep[];
  persistedToolCalls?: ToolCallRecord[];
}

function getProductName(toolName: string): string {
  const match = toolName.match(/^(splunk|okta|cs|sentinel|defender|elastic|opensearch)_/);
  if (!match?.[1]) return '';
  const map: Record<string, string> = {
    splunk: 'Splunk', okta: 'Okta', cs: 'CrowdStrike',
    sentinel: 'Sentinel', defender: 'Defender',
    elastic: 'Elasticsearch', opensearch: 'OpenSearch',
  };
  return map[match[1]] ?? '';
}

const ACRONYMS: Record<string, string> = { cve: 'CVE', ip: 'IP', dns: 'DNS', url: 'URL', ioc: 'IOC', ttps: 'TTPs' };

function getDisplayName(toolName: string): string {
  return toolName.split('_').map(w => ACRONYMS[w] ?? w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function getLastSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  // Find the last sentence boundary
  const sentences = trimmed.split(/(?<=[.!?])\s+/).filter(Boolean);
  return sentences[sentences.length - 1] ?? trimmed.slice(-80);
}

const OUTPUT_COLLAPSED_LINES = 8;

function ToolOutputBlock({ result }: { result: string }) {
  const [outputExpanded, setOutputExpanded] = useState(false);

  // Try to pretty-print JSON, fall back to raw string
  let formatted = result;
  try {
    const parsed = JSON.parse(result);
    formatted = JSON.stringify(parsed, null, 2);
  } catch {
    // not JSON, use as-is
  }

  const lines = formatted.split('\n');
  const isLong = lines.length > OUTPUT_COLLAPSED_LINES;
  const displayText = isLong && !outputExpanded
    ? lines.slice(0, OUTPUT_COLLAPSED_LINES).join('\n')
    : formatted;

  return (
    <div className="reasoning-tool-output">
      <div className="reasoning-tool-output-header">
        <span className="reasoning-tool-output-label">Output</span>
        {isLong && (
          <button
            className="reasoning-tool-output-toggle"
            onClick={() => setOutputExpanded(!outputExpanded)}
            type="button"
          >
            {outputExpanded ? 'Collapse' : `Show all (${lines.length} lines)`}
          </button>
        )}
      </div>
      <div className={`reasoning-tool-output-body ${isLong && !outputExpanded ? 'collapsed' : ''}`}>
        {displayText}
      </div>
    </div>
  );
}

function ToolCallBlock({ tc }: { tc: ToolCallEntry }) {
  const product = getProductName(tc.toolName);
  const displayName = getDisplayName(tc.toolName);
  const hasQuery = tc.queryLanguage && tc.inputPayload;
  const queryField = tc.inputPayload?.['query'] ?? tc.inputPayload?.['search'] ?? tc.inputPayload?.['search_query'];
  const queryText = typeof queryField === 'string' ? queryField : null;

  return (
    <div className="reasoning-tool-block">
      <div className="reasoning-tool-header">
        {tc.status === 'running' && <span className="reasoning-tool-spinner" />}
        {tc.status === 'success' && <span className="reasoning-tool-check">✓</span>}
        {tc.status === 'error' && <span className="reasoning-tool-error">✗</span>}
        <span className="reasoning-tool-name">{displayName}</span>
        {product && <span className="reasoning-tool-connector">{product}</span>}
      </div>

      {hasQuery && queryText ? (
        <QueryBlock code={queryText} language={tc.queryLanguage as QueryLanguage} />
      ) : tc.inputPayload ? (
        <div className="reasoning-tool-params">
          {JSON.stringify(tc.inputPayload, null, 2)}
        </div>
      ) : null}

      {tc.resultSummary && tc.status !== 'running' && (
        <ToolOutputBlock result={tc.resultSummary} />
      )}
    </div>
  );
}

function PersistedToolCallBlock({ tc }: { tc: ToolCallRecord }) {
  const product = getProductName(tc.toolName);
  const displayName = getDisplayName(tc.toolName);
  const queryField = tc.inputPayload?.['query'] ?? tc.inputPayload?.['search'] ?? tc.inputPayload?.['search_query'];
  const queryText = typeof queryField === 'string' ? queryField : null;

  return (
    <div className="reasoning-tool-block">
      <div className="reasoning-tool-header">
        {tc.success ? (
          <span className="reasoning-tool-check">✓</span>
        ) : (
          <span className="reasoning-tool-error">✗</span>
        )}
        <span className="reasoning-tool-name">{displayName}</span>
        {product && <span className="reasoning-tool-connector">{product}</span>}
      </div>

      {tc.queryLanguage && queryText ? (
        <QueryBlock code={queryText} language={tc.queryLanguage as QueryLanguage} />
      ) : tc.inputPayload ? (
        <div className="reasoning-tool-params">
          {JSON.stringify(tc.inputPayload, null, 2)}
        </div>
      ) : null}

      {tc.resultSummary && (
        <ToolOutputBlock result={tc.resultSummary} />
      )}
    </div>
  );
}

export function ReasoningTrace({
  reasoningText,
  toolCalls,
  traceEntries,
  isStreaming = false,
  persistedTrace,
  persistedToolCalls,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  // Streaming mode: use typewriter for thinking text
  const displayedText = useTypewriter(reasoningText ?? '', isStreaming);

  // Build the summary text for the collapsed bar
  const lastStepText = useMemo(() => {
    if (persistedTrace && persistedTrace.length > 0) {
      // Check the last step — if it's a tool_call, show tool info
      const lastStep = persistedTrace[persistedTrace.length - 1];
      if (lastStep?.type === 'tool_call' && persistedToolCalls) {
        const matchingTc = persistedToolCalls.find(
          (tc) => tc.toolUseId === lastStep.content || tc.toolName === lastStep.content,
        );
        if (matchingTc) {
          const name = getProductName(matchingTc.toolName) || getDisplayName(matchingTc.toolName);
          return matchingTc.success ? `Used ${name}` : `${name} failed`;
        }
      }
      const thinkingSteps = persistedTrace.filter((s) => s.type === 'thinking');
      const lastThinking = thinkingSteps[thinkingSteps.length - 1];
      return lastThinking?.content ? getLastSentence(lastThinking.content) : '';
    }

    // Streaming mode
    const text = reasoningText ?? '';
    return getLastSentence(text);
  }, [persistedTrace, persistedToolCalls, reasoningText]);

  // Determine if there's anything to show
  const hasContent = (reasoningText && reasoningText.length > 0) ||
    (toolCalls && toolCalls.size > 0) ||
    (persistedTrace && persistedTrace.length > 0);

  if (!hasContent) return null;

  // Collapse bar text: current step during streaming, last step when complete
  const barText = useMemo(() => {
    if (isStreaming && traceEntries && traceEntries.length > 0) {
      const lastEntry = traceEntries[traceEntries.length - 1];
      if (lastEntry?.type === 'tool' && lastEntry.toolUseId) {
        const tc = toolCalls?.get(lastEntry.toolUseId);
        if (tc) {
          const name = getProductName(tc.toolName) || getDisplayName(tc.toolName);
          if (tc.status === 'running') return `Waiting on ${name}...`;
          if (tc.status === 'success') return `Analyzing ${name} results...`;
          if (tc.status === 'error') return `${name} failed`;
        }
      }
      return getLastSentence(displayedText);
    }
    return isStreaming ? getLastSentence(displayedText) : lastStepText;
  }, [isStreaming, traceEntries, toolCalls, displayedText, lastStepText]);

  return (
    <div className="reasoning-inline">
      <button
        className="reasoning-collapse-bar"
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        <span className="reasoning-status-icon">
          {isStreaming ? (
            <span className="reasoning-status-spinner" />
          ) : (
            <span className="reasoning-status-check">✓</span>
          )}
        </span>
        <span className={`reasoning-step-text ${isStreaming ? 'streaming' : ''}`}>
          {barText || (isStreaming ? 'Thinking…' : '')}
        </span>
        <span className={`reasoning-chevron ${expanded ? 'expanded' : ''}`}>▾</span>
      </button>

      {expanded && (
        <div className="reasoning-trace-body">
          {/* Persisted mode: render from persisted trace */}
          {persistedTrace && persistedTrace.length > 0 ? (
            <>
              {persistedTrace.map((step, i) => {
                if (step.type === 'thinking' || step.type === 'conclusion') {
                  return (
                    <div key={i} className="reasoning-thinking-step">
                      {step.content}
                    </div>
                  );
                }
                if (step.type === 'tool_call') {
                  // Find matching persisted tool call
                  const matchingTc = persistedToolCalls?.find(
                    (tc) => tc.toolName === step.content || tc.toolUseId === step.content,
                  );
                  if (matchingTc) {
                    return <PersistedToolCallBlock key={i} tc={matchingTc} />;
                  }
                  return (
                    <div key={i} className="reasoning-thinking-step">
                      {step.content}
                    </div>
                  );
                }
                return null;
              })}
            </>
          ) : (
            <>
              {/* Streaming mode: render interleaved thinking + tool calls */}
              {traceEntries && traceEntries.length > 0 ? (
                traceEntries.map((entry, i) => {
                  if (entry.type === 'thinking') {
                    const isLastEntry = i === traceEntries.length - 1;
                    return (
                      <div key={`thinking-${i}`} className="reasoning-thinking-step">
                        {entry.text}
                        {isStreaming && isLastEntry && <span className="cursor-blink">▌</span>}
                      </div>
                    );
                  }
                  if (entry.type === 'tool' && entry.toolUseId) {
                    const tc = toolCalls?.get(entry.toolUseId);
                    if (tc) return <ToolCallBlock key={entry.toolUseId} tc={tc} />;
                  }
                  return null;
                })
              ) : (
                <div className="reasoning-thinking-step">
                  Thinking…{isStreaming && <span className="cursor-blink">▌</span>}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
